import React from 'react';
import ImageC from 'src/components/Image';

import classes from './index.module.css';
import cn from "src/utils/cn";

export type ChipTheme = 'primary' | 'warning' | 'danger' | 'neutral';

export interface ChipProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
	onClick?: React.MouseEventHandler<HTMLDivElement>;
	className?: string;
	icon?: React.ReactNode | string | null | false;
	text: string;
	theme?: ChipTheme;
	iconPosition?: 'left' | 'right';
	active?: boolean;
}

const themeClasses = {
	primary: {
		border: 'border-primary',
		text: 'text-primary',
		defaultIcon: 'information-primary',
	},
	warning: {
		border: 'border-warning',
		text: 'text-warning',
		defaultIcon: 'warning-yellow',
	},
	danger: {
		border: 'border-danger',
		text: 'text-danger',
		defaultIcon: 'close-circle',
	},
	neutral: {
		border: 'border-gray',
		text: 'text-neutral-5',
		defaultIcon: null,
	},
};

export default function Chip({
	onClick,
	className,
	icon,
	text,
	theme = 'primary',
	iconPosition = 'right',
	style,
	active,
	...props
}: ChipProps) {
	const activeTheme = themeClasses[theme] || themeClasses.primary;
	const activeIcon = icon !== undefined ? icon : activeTheme.defaultIcon;
	const showIcon = activeIcon !== null && activeIcon !== false && activeIcon !== '';

	const iconContent = (() => {
		if (!showIcon) return null;

		if (typeof activeIcon === 'string') {
			const src =
				activeIcon.startsWith('/') || activeIcon.startsWith('http')
					? activeIcon
					: `/icons/${activeIcon}.svg`;
			return (
				<div
					className={cn('position-relative', {
						'ml-1': iconPosition === 'right',
						'mr-1': iconPosition === 'left',
					})}
					style={{
						width: 16,
						height: 16,
					}}
				>
					<ImageC layout="fill" objectFit="contain" src={src} />
				</div>
			);
		}

		return (
			<div
				className={cn({
					'ml-1': iconPosition === 'right',
					'mr-1': iconPosition === 'left',
				})}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				{activeIcon}
			</div>
		);
	})();

	return (
		<div
			className={cn(
				'rounded-pill border-1 p-1 px-2 d-flex align-items-center text-nowrap',
				classes.chip,
				classes[`theme-${theme}`],
				activeTheme.border,
				{
					['cursor-default']: !onClick,
					['cursor-pointer']: !!onClick,
					[classes['chip-interactive']]: !!onClick,
					[classes['chip-active']]: !!active,
				},
				className,
			)}
			onClick={onClick}
			style={{
				width: 'fit-content',
				...style,
			}}
			{...props}
		>
			{iconPosition === 'left' && iconContent}
			<span className={cn(activeTheme.text, 'fw-600 fs-12')}>
				{text}
			</span>
			{iconPosition === 'right' && iconContent}
		</div>
	);
}

